import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Form, Input, Select, message, Card, Tag, Popconfirm, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, UserOutlined } from '@ant-design/icons';
import { studentAPI, classAPI } from '../services/api';
import { Student, Class as ClassType } from '../types';
import './StudentManagement.css';
import {
  buildFilters,
  compareDate,
  compareNumber,
  compareText,
  createValueFilter,
  getScoreBand,
  SCORE_BAND_FILTERS,
} from '../utils/tableHelpers';

const { Option } = Select;
const { Search } = Input;

// Class 类型已从 types/index.ts 导入

const StudentManagement: React.FC = () => {
  const [students, setStudents] = useState<Student[]>([]);
  const [classes, setClasses] = useState<ClassType[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [searchText, setSearchText] = useState('');
  const [selectedClass, setSelectedClass] = useState<number | undefined>(undefined);
  const [form] = Form.useForm();

  // 获取学生列表
  const fetchStudents = async () => {
    setLoading(true);
    try {
      const params: any = { page_size: 100 };
      if (selectedClass) {
        params.class_id = selectedClass;
      }
      if (searchText) {
        params.search = searchText;
      }
      const response = await studentAPI.list(params);
      setStudents(response.data.data || []);
    } catch (error) {
      message.error('获取学生列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取班级列表
  const fetchClasses = async () => {
    try {
      const response = await classAPI.list({ page_size: 100 });
      setClasses(response.data.data || []);
    } catch (error) {
      message.error('获取班级列表失败');
    }
  };

  useEffect(() => {
    fetchClasses();
    fetchStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClass, searchText]);

  // 显示创建/编辑模态框
  const showModal = (student?: Student) => {
    if (student) {
      setEditingStudent(student);
      form.setFieldsValue({
        name: student.name,
        class_id: student.class_id,
      });
    } else {
      setEditingStudent(null);
      form.resetFields();
    }
    setModalVisible(true);
  };

  // 处理表单提交
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingStudent) {
        // 更新学生
        await studentAPI.update(editingStudent.id, values);
        message.success('学生信息更新成功');
      } else {
        // 创建学生
        await studentAPI.create(values);
        message.success('学生创建成功');
      }
      
      setModalVisible(false);
      form.resetFields();
      fetchStudents();
    } catch (error) {
      message.error(editingStudent ? '更新学生信息失败' : '创建学生失败');
    }
  };

  // 删除学生
  const handleDelete = async (id: number) => {
    try {
      await studentAPI.delete(id);
      message.success('学生删除成功');
      fetchStudents();
    } catch (error) {
      message.error('删除学生失败');
    }
  };

  // 搜索处理
  const handleSearch = (value: string) => {
    setSearchText(value);
  };

  // 年级显示
  const getGradeLabel = (grade?: number) => {
    if (!grade) return '-';
    const gradeLabels: { [key: number]: string } = {
      1: '一年级', 2: '二年级', 3: '三年级',
      4: '四年级', 5: '五年级', 6: '六年级',
      7: '初一', 8: '初二', 9: '初三',
      10: '高一', 11: '高二', 12: '高三',
    };
    return gradeLabels[grade] || `${grade}年级`;
  };

  // 表格列定义
  const columns = [
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      sorter: compareText((record: Student) => record.name),
      filters: buildFilters(students, (record: Student) => record.name),
      onFilter: createValueFilter((record: Student) => record.name),
      filterSearch: true,
      render: (text: string) => (
        <Space>
          <UserOutlined />
          <strong>{text}</strong>
        </Space>
      ),
    },
    {
      title: '班级',
      key: 'class',
      sorter: compareText((record: Student) => record.class?.name),
      filters: buildFilters(students, (record: Student) => record.class?.name, {
        includeEmpty: true,
        emptyText: '未分班',
      }),
      onFilter: createValueFilter((record: Student) => record.class?.name),
      filterSearch: true,
      render: (text: string, record: Student) => {
        const className = record.class?.name || '-';
        const grade = record.class?.grade;
        return (
          <Space>
            {grade && <Tag color="blue">{getGradeLabel(grade)}</Tag>}
            <span>{className}</span>
          </Space>
        );
      },
    },
    {
      title: '作文数量',
      key: 'essay_count',
      sorter: compareNumber((record: Student) => record.stats?.essay_count),
      render: (text: string, record: Student) => (
        <Tooltip title="该学生提交的作文总数">
          <span>{record.stats?.essay_count || 0} 篇</span>
        </Tooltip>
      ),
    },
    {
      title: '平均分',
      key: 'average_score',
      sorter: compareNumber((record: Student) => record.stats?.average_score),
      filters: SCORE_BAND_FILTERS,
      onFilter: (value: boolean | string | number | bigint, record: Student) => (
        getScoreBand(record.stats?.average_score) === String(value)
      ),
      render: (text: string, record: Student) => {
        const score = record.stats?.average_score;
        if (!score) return '-';
        const color = score >= 20 ? 'green' : score >= 15 ? 'orange' : 'red';
        return (
          <Tag color={color}>
            {score.toFixed(1)} 分
          </Tag>
        );
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: compareDate((record: Student) => record.created_at),
      render: (text: string) => new Date(text).toLocaleDateString(),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: Student) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => showModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个学生吗？"
            description="删除后，该学生的作文记录将被保留但不再关联到此学生。"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="student-management">
      <Card
        title="学生管理"
        extra={
          <Space>
            <Select
              style={{ width: 200 }}
              placeholder="选择班级筛选"
              allowClear
              onChange={(value) => setSelectedClass(value)}
            >
              {classes.map(cls => (
                <Option key={cls.id} value={cls.id}>
                  {cls.name}
                </Option>
              ))}
            </Select>
            <Search
              placeholder="搜索学生姓名"
              onSearch={handleSearch}
              style={{ width: 200 }}
              enterButton={<SearchOutlined />}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => showModal()}
            >
              添加学生
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={students}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 名学生`,
          }}
        />
      </Card>

      <Modal
        title={editingStudent ? '编辑学生信息' : '添加学生'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        okText="确定"
        cancelText="取消"
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="name"
            label="学生姓名"
            rules={[{ required: true, message: '请输入学生姓名' }]}
          >
            <Input placeholder="请输入学生姓名" />
          </Form.Item>

          <Form.Item
            name="class_id"
            label="所属班级"
            rules={[{ required: true, message: '请选择班级' }]}
          >
            <Select placeholder="请选择班级">
              {classes.map(cls => (
                <Option key={cls.id} value={cls.id}>
                  {cls.name} ({getGradeLabel(cls.grade)})
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default StudentManagement;
